import { events } from '../core/events.js';
import { setFlag, hasFlag } from './state.js';
import { isCommPart, allPartsCollected, partsCollectedCount } from './commParts.js';
import { showDialog } from '../ui/dialog.js';
import { blockGameInput } from '../input.js';

// A small scripted narrative. Beats either fire on start or via events. Each
// beat sets a flag so it only runs once per session.

export function initStory() {
  // -------- Intro (fires after the camera's orbit-to-surface sweep) --------
  // Input stays blocked by the title screen until this dialog closes.
  // If the flag was somehow already set, release input explicitly so the
  // player isn't stuck.
  events.on('camera:intro:done', () => {
    if (hasFlag('intro_seen')) {
      blockGameInput(false);
      return;
    }
    showDialog([
      { speaker: 'LOG', text: 'Wake up, commander. Life support\'s holding.' },
      { speaker: 'LOG', text: 'You don\'t recognise the sky.' },
      { speaker: 'LOG', text: 'W/S to run, A/D to turn, space to jump. E to pick things up.' },
    ]).then(() => setFlag('intro_seen'));
  });

  // -------- First time the player discovers their crashed pod --------
  events.on('pod:discovered', async () => {
    if (hasFlag('pod_discovered')) return;
    setFlag('pod_discovered');
    await showDialog([
      { speaker: 'LOG', text: 'Your pod. Didn\'t so much land as dig itself in.' },
      { speaker: 'LOG', text: 'Hull\'s breached. Comms scattered across half the moon.' },
      { speaker: 'LOG', text: 'You\'re not flying anywhere. So you walk.' },
      { speaker: 'LOG', text: 'Find the pieces. We\'ll see what we can do.' },
    ]);
  });

  // -------- Part pickups --------
  events.on('inventory:added', async ({ id }) => {
    if (!isCommPart(id)) return;

    // First part: explain what it's for.
    if (!hasFlag('first_part')) {
      setFlag('first_part');
      await showDialog([
        { speaker: 'LOG', text: 'Comm hardware. Three more and the antenna might breathe again.' },
      ]);
      return;
    }

    // Last part: prompt the repair.
    if (allPartsCollected() && !hasFlag('all_parts')) {
      setFlag('all_parts');
      await showDialog([
        { speaker: 'LOG', text: `${partsCollectedCount()} pieces. That\'s all of them.` },
        { speaker: 'LOG', text: 'Back to the pod. Time to try the repair.' },
      ]);
    }
  });

  // -------- First time the ruins come into range --------
  events.on('ruins:discovered', async () => {
    if (hasFlag('ruins_discovered')) return;
    setFlag('ruins_discovered');
    await showDialog([
      { speaker: 'LOG', text: 'Stonework. Older than the crash.' },
      { speaker: 'LOG', text: 'Three hollows in the altar. Each one glowing a different colour.' },
    ]);
  });

  // -------- Final socket filled — puzzle solved --------
  events.on('ruins:solved', async () => {
    if (hasFlag('ruins_solved')) return;
    setFlag('ruins_solved');
    await showDialog([
      { speaker: 'LOG', text: 'The altar remembers. Something opens.' },
    ]);
  });

  // -------- First time the pond is discovered --------
  events.on('pond:discovered', async () => {
    if (hasFlag('pond_discovered')) return;
    setFlag('pond_discovered');
    await showDialog([
      { speaker: 'LOG', text: 'A pond. Fish circling a raft in the middle.' },
      { speaker: 'LOG', text: 'Can\'t swim in this suit.' },
    ]);
  });

  // -------- Lure cast — raft starts drifting --------
  events.on('pond:lure-cast', async () => {
    if (hasFlag('pond_lure_cast')) return;
    setFlag('pond_lure_cast');
    await showDialog([
      { speaker: 'LOG', text: 'Fish swarm the bloom. The raft\'s drifting in.' },
    ]);
  });

  // -------- First time the cave is entered --------
  events.on('cave:discovered', async () => {
    if (hasFlag('cave_discovered')) return;
    setFlag('cave_discovered');
    await showDialog([
      { speaker: 'LOG', text: 'A ring of stones. Something glinting at the back.' },
      { speaker: 'LOG', text: 'Sleepers between you and it. Tread lightly.' },
    ]);
  });

  // -------- First time the player wakes a guardian --------
  events.on('cave:disturbed', async () => {
    if (hasFlag('cave_disturbed')) return;
    setFlag('cave_disturbed');
    await showDialog([
      { speaker: 'LOG', text: 'They\'re awake. Back off.' },
    ]);
  });

  // -------- First time the board carrier is seen up close --------
  events.on('creatures:carrier-spotted', async () => {
    if (hasFlag('carrier_spotted')) return;
    setFlag('carrier_spotted');
    await showDialog([
      { speaker: 'LOG', text: 'One of them\'s carrying something on its back. Looks salvageable.' },
      { speaker: 'LOG', text: 'Fast. Bolts on sight.' },
    ]);
  });

  // -------- First successful transmission --------
  events.on('communicator:repaired', async () => {
    await showDialog([
      { speaker: 'COMMS', text: '... kzzt ... BASE STATION: Unit 47, do you copy?' },
      { speaker: 'COMMS', text: 'BASE STATION: Good to hear you, commander. It\'s been a while.' },
      { speaker: 'COMMS', text: 'BASE STATION: Rescue vessel inbound. Sit tight.' },
      { speaker: 'LOG',   text: 'You hadn\'t realised how quiet it had gotten.' },
    ]);
    // Hand off to the ending cutscene (rescue ship lands + takes off).
    events.emit('rescue:arriving');
  });
}
